import { HOST_CONFIG, DEFAULT_VALUES } from '../constants/themeConstants';

/**
 * Utilitários para operações relacionadas a temas
 */

/**
 * Converte cor hex para objeto RGB
 * @param {string} hex
 * @returns {Object} {r, g, b}
 */
const hexToRgb = (hex) => {
  const cleanHex = hex.replace("#", "");
  const r = parseInt(cleanHex.substr(0, 2), 16);
  const g = parseInt(cleanHex.substr(2, 2), 16);
  const b = parseInt(cleanHex.substr(4, 2), 16);
  return { r, g, b };
};

/**
 * Converte objeto RGB para hex
 * @param {number} r
 * @param {number} g
 * @param {number} b
 * @returns {string} hex
 */
const rgbToHex = (r, g, b) => {
  return "#" + [r, g, b].map(x => {
    const hex = Math.max(0, Math.min(255, Math.round(x))).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  }).join("");
};

/**
 * Escurece uma cor hex em uma porcentagem
 * @param {string} color - Cor hex
 * @param {number} percent - Porcentagem (0-100)
 * @returns {string} Nova cor hex
 */
const darkenColor = (color, percent) => {
  const { r, g, b } = hexToRgb(color);
  const factor = 1 - (percent / 100);
  return rgbToHex(r * factor, g * factor, b * factor);
};

/**
 * Calcula a melhor cor de texto (branco ou preto) para um fundo dado
 * Usando fórmula de luminância relativa (W3C)
 * @param {string} backgroundColor - Cor de fundo hex
 * @returns {string} Cor do texto (#ffffff ou #000000)
 */
export const getContrastColor = (backgroundColor) => {
  if (!backgroundColor) return '#000000';
  
  const { r, g, b } = hexToRgb(backgroundColor);
  
  // Fórmula de luminância relativa: 0.2126 * R + 0.7152 * G + 0.0722 * B
  // As cores RGB devem ser normalizadas antes (sRGB) mas para fins de UI simples
  // a fórmula direta costuma funcionar bem o suficiente
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Se luminância > 0.5, a cor é clara, então use texto preto
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

/**
 * Gera um gradient dinâmico baseado na cor primária
 * @param {string} primaryColor - Cor primária em formato hex
 * @returns {string} CSS gradient
 */
export const generateDynamicGradient = (primaryColor) => {
  const hex = primaryColor.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Criar uma versão mais escura (30% mais escura)
  const darkerR = Math.max(0, Math.floor(r * 0.7));
  const darkerG = Math.max(0, Math.floor(g * 0.7));
  const darkerB = Math.max(0, Math.floor(b * 0.7));

  const darkerHex = `#${darkerR.toString(16).padStart(2, "0")}${darkerG
    .toString(16)
    .padStart(2, "0")}${darkerB.toString(16).padStart(2, "0")}`;

  return `linear-gradient(90deg, ${primaryColor} 0%, ${darkerHex} 100%)`;
};

/**
 * Aplica cores de whitelabel ao tema base
 * @param {Object} baseTheme - Tema base (light ou dark)
 * @param {Object} tenantColors - Cores do tenant
 * @returns {Object} Tema customizado
 */
export const applyWhitelabelColors = (baseTheme, tenantColors) => {
  if (!tenantColors?.primary) {
    return baseTheme;
  }

  const dynamicGradient = generateDynamicGradient(tenantColors.primary);
  const sidebarBackground = tenantColors.primary;
  const sidebarTextColor = getContrastColor(sidebarBackground);

  return {
    ...baseTheme,
    primary: tenantColors.primary,
    // Se quiser aplicar no texto primário geral, mantenha; senão, remova a linha abaixo
    // textPrimary: tenantColors.primary, 
    primaryGradient: dynamicGradient,
    
    // Sidebar com a cor do cliente
    sidebarBackground: sidebarBackground,
    
    // Cores do menu adaptadas para contraste
    menuItemText: sidebarTextColor,
    menuItemHoverText: sidebarTextColor,
    menuItemSelectedText: sidebarTextColor,
    menuItemActiveText: sidebarTextColor,
    
    // Ícones e outros elementos da sidebar
    sidebarIconColor: sidebarTextColor,
    
    // Ajustes de hover/active backgrounds para serem versões mais claras/escuras da cor primária
    menuItemHoverBackground: darkenColor(tenantColors.primary, 10),
    menuItemSelectedBackground: darkenColor(tenantColors.primary, 15),
    menuItemActiveBackground: darkenColor(tenantColors.primary, 15),
  };
};

/**
 * Extrai o identificador do tenant da URL
 * @param {string} host - Host da URL (opcional, usa window.location.host se não fornecido)
 * @returns {string|null} Identificador do tenant ou null
 */
export const extractTenantFromUrl = (host = null) => {
  const currentHost = host || window.location.host;
  
  const hostParts = currentHost.split(".");
  
  const EXCLUDED_IDENTIFIERS = ['v2', 'v3', 'localhost', 'staging', 'stagingv2', 'prod2','app'];

  const isAmpulheta = currentHost.includes('bi.atfbusiness');

  const isEmin = currentHost.includes('bi.controllup');

  if (isAmpulheta) {
    return 'ampulheta';
  }

  if (isEmin) {
    return 'emin';
  }

  if (hostParts.length >= HOST_CONFIG.MIN_HOST_PARTS &&
      hostParts[1] === HOST_CONFIG.DOMAIN_IDENTIFIER) {
      if (!EXCLUDED_IDENTIFIERS.includes(hostParts[0])) {
        return hostParts[0];
      }
    }
    
    return null;
};

/**
 * Atualiza o favicon da página
 * @param {string} faviconUrl - URL do favicon
 */
export const updateFavicon = (faviconUrl) => {
  if (!faviconUrl) return;

  const link = document.querySelector("link[rel*='icon']") || document.createElement("link");
  link.type = "image/x-icon";
  link.rel = "shortcut icon";
  link.href = faviconUrl;

  const oldLink = document.querySelector("link[rel*='icon']");
  if (oldLink) {
    oldLink.remove();
  }

  document.getElementsByTagName("head")[0].appendChild(link);
};

/**
 * Atualiza o título da página
 * @param {string} title - Novo título
 */
export const updatePageTitle = (title) => {
  if (title) {
    document.title = title;
  }
};

/**
 * Cria um objeto de tema do tenant formatado
 * @param {string} tenantId - ID do tenant
 * @param {Object} tenantData - Dados do tenant da API
 * @returns {Object} Objeto de tema formatado
 */
export const createTenantThemeObject = (tenantId, tenantData) => {
  const backendTheme = tenantData.theme || {};
  
  return {
    id: tenantId,
    name: tenantData.nome || tenantData.name || DEFAULT_VALUES.TENANT_NAME_FALLBACK,
    colors: {
      primary: backendTheme.colors?.primary || DEFAULT_VALUES.PRIMARY_COLOR,
      secondary: backendTheme.colors?.secondary || DEFAULT_VALUES.SECONDARY_COLOR,
      gradient1: backendTheme.colors?.gradient1 || backendTheme.colors?.primary || DEFAULT_VALUES.PRIMARY_COLOR,
      gradient2: backendTheme.colors?.gradient2 || backendTheme.colors?.secondary || DEFAULT_VALUES.SECONDARY_COLOR,
    },
    logo: backendTheme.logo || null,
    fav_icon: backendTheme.favIcon || backendTheme.logo || DEFAULT_VALUES.FAVICON_PATH,
  };
};

/**
 * Verifica se dois objetos de tema são diferentes
 * @param {Object} theme1 - Primeiro tema
 * @param {Object} theme2 - Segundo tema
 * @returns {boolean} True se são diferentes
 */
export const areThemesDifferent = (theme1, theme2) => {
  return JSON.stringify(theme1) !== JSON.stringify(theme2);
};